// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a link shown as a chip in the composer, e.g. another agent chat or a pull request mentioned
// in an Agents prompt. The text holds it as the Markdown link `[label](url)`, so drafts, the outbox
// and the sent message keep it; composers that take chips (linkChips) turn such links back into
// chips when their text is set. How a chip looks is up to the composer (ComposerLinkChipContext).

import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
	$getNodeByKey,
	DecoratorNode,
	type DOMExportOutput,
	type LexicalNode,
	type NodeKey,
	type SerializedLexicalNode,
	type Spread,
} from 'lexical';
import {createContext, type JSX, type ReactNode, useContext} from 'react';

/** Draws a chip; `remove` takes it out of the composer. Without a provider it shows as its label. */
export const ComposerLinkChipContext = createContext<
	((chip: {label: string; url: string; remove: () => void}) => ReactNode) | null
>(null);

export type SerializedComposerLinkChipNode = Spread<{label: string; url: string}, SerializedLexicalNode>;

/** The Markdown link a chip stands for. Brackets and line breaks can't be in its label. */
export function linkChipText(label: string, url: string): string {
	return `[${label.replace(/[[\]\r\n]/g, ' ').trim()}](${url})`;
}

const LINK = /\[([^\]\n]{1,200})\]\(([^)\s]{1,500})\)/g;

/** Splits text into plain runs and the links `takes` accepts as chips. */
export function splitLinkChips(
	text: string,
	takes: (url: string) => boolean,
): Array<{text: string} | {label: string; url: string}> {
	const out: Array<{text: string} | {label: string; url: string}> = [];
	let at = 0;
	for (const match of text.matchAll(LINK)) {
		const [whole, label, url] = match;
		if (!takes(url!)) continue;
		if (match.index! > at) out.push({text: text.slice(at, match.index)});
		out.push({label: label!, url: url!});
		at = match.index! + whole.length;
	}
	if (at < text.length) out.push({text: text.slice(at)});
	return out;
}

function ComposerLinkChip({label, url, nodeKey}: {label: string; url: string; nodeKey: NodeKey}) {
	const [editor] = useLexicalComposerContext();
	const render = useContext(ComposerLinkChipContext);
	const remove = () =>
		editor.update(() => {
			$getNodeByKey(nodeKey)?.remove();
		});
	return <>{render ? render({label, url, remove}) : label}</>;
}

export class ComposerLinkChipNode extends DecoratorNode<JSX.Element> {
	__label: string;
	__url: string;

	static override getType(): string {
		return 'composer-link-chip';
	}

	static override clone(node: ComposerLinkChipNode): ComposerLinkChipNode {
		return new ComposerLinkChipNode(node.__label, node.__url, node.__key);
	}

	static override importJSON(serializedNode: SerializedComposerLinkChipNode): ComposerLinkChipNode {
		return $createComposerLinkChipNode(serializedNode.label, serializedNode.url);
	}

	constructor(label: string, url: string, key?: NodeKey) {
		super(key);
		this.__label = label;
		this.__url = url;
	}

	override exportJSON(): SerializedComposerLinkChipNode {
		return {...super.exportJSON(), label: this.__label, url: this.__url};
	}

	override createDOM(): HTMLElement {
		const element = document.createElement('span');
		element.setAttribute('data-lexical-link-chip', this.__url);
		return element;
	}

	override updateDOM(): boolean {
		return false;
	}

	override exportDOM(): DOMExportOutput {
		const element = document.createElement('span');
		element.textContent = this.getTextContent();
		return {element};
	}

	override getTextContent(): string {
		const latest = this.getLatest();
		return linkChipText(latest.__label, latest.__url);
	}

	override isInline(): true {
		return true;
	}

	override decorate(): JSX.Element {
		return <ComposerLinkChip label={this.__label} url={this.__url} nodeKey={this.__key} />;
	}
}

export function $createComposerLinkChipNode(label: string, url: string): ComposerLinkChipNode {
	return new ComposerLinkChipNode(label.replace(/[[\]\r\n]/g, ' ').trim(), url);
}

export function $isComposerLinkChipNode(node: LexicalNode | null | undefined): node is ComposerLinkChipNode {
	return node instanceof ComposerLinkChipNode;
}
